// Turns program, Anchor, wallet and RPC failures into readable English messages.
import rawIdl from "./idl.json";

// Friendlier wording for the program's own errors (falls back to the IDL msg).
const FRIENDLY = {
  LaunchesPaused: "New launches are paused by the platform right now.",
  NameTooLong: "The token name is too long (max 32 bytes).",
  SymbolTooLong: "The ticker is too long (max 10 bytes).",
  UriTooLong: "The metadata link is too long (max 200 bytes).",
  ZeroAmount: "Enter an amount greater than zero.",
  ZeroOutput: "This trade is too small to produce anything. Try a larger amount.",
  SlippageExceeded: "Price moved more than your slippage tolerance. Try again or raise slippage.",
  InsufficientCurveTokens: "Not enough tokens left on the curve for this buy. Try a smaller amount.",
  InsufficientCurveSol: "The curve does not hold enough SOL for this sell. Try a smaller amount.",
  InvariantViolated: "The curve rejected this trade (invariant check). Try a different amount.",
  MathOverflow: "Amount is too large.",
  StakeTooSmall: "Your stake in the well must be at least 1 whole token.",
  RemainderTooSmall: "What stays in the well must be zero or at least 1 whole token. Unstake everything or leave at least 1.",
  InsufficientStake: "You don't have that many tokens in the well.",
  NothingToClaim: "There is no water to claim yet.",
  WellUnderfunded: "The well holds less SOL than accounted. Please try again later.",
  DevBuyTooLarge: "Dev buy cannot exceed 15% of the supply.",
};

const PROGRAM_ERRORS = new Map(
  (rawIdl.errors || []).map((e) => [e.code, { name: e.name, msg: FRIENDLY[e.name] || e.msg }])
);

// A few Anchor framework codes that users can realistically hit.
const ANCHOR_ERRORS = {
  2000: "A required account constraint failed.",
  2003: "Account constraint failed (raw).",
  2006: "Account address mismatch. If you were launching, another launch landed first - please try again.",
  2012: "Account address mismatch.",
  2014: "Token account does not match the expected mint.",
  2015: "Token account does not have the expected owner.",
  3001: "Account discriminator mismatch.",
  3007: "Account is owned by a different program.",
  3012: "A required account does not exist yet.",
  3014: "Wrong program ID.",
};

const SYSTEM_HINTS = [
  [/insufficient (funds|lamports)/i, "Not enough SOL in your wallet to cover this transaction and fees."],
  [/custom program error: 0x1\b/i, "Not enough SOL (or tokens) in your wallet for this transaction."],
  [/Attempt to debit an account but found no record of a prior credit/i, "Your wallet has no SOL on this network. Fund it first."],
  [/already in use/i, "That account already exists. Please retry."],
  [/Blockhash not found|block height exceeded|expired/i, "The transaction expired before it landed. Please try again."],
  [/exceeded CUs meter|computational budget exceeded/i, "The transaction ran out of compute. Please try again."],
  [/User rejected|rejected the request|Transaction cancelled|declined/i, "You cancelled the request in your wallet."],
  [/429|Too many requests|rate limit/i, "The RPC is rate limiting us. Wait a few seconds and try again."],
  [/Failed to fetch|NetworkError|fetch failed/i, "Network error while talking to the Solana RPC."],
];

export class TxError extends Error {
  constructor(message, { logs, signature, code } = {}) {
    super(message);
    this.logs = logs;
    this.signature = signature;
    this.code = code;
  }
}

/** Extract a custom error code from a transaction `err` object. */
export function customCodeFromErr(err) {
  if (!err || typeof err !== "object") return null;
  const ie = err.InstructionError;
  if (Array.isArray(ie) && ie[1] && typeof ie[1] === "object" && "Custom" in ie[1]) return ie[1].Custom;
  return null;
}

export function messageForCode(code) {
  if (code == null) return null;
  if (PROGRAM_ERRORS.has(code)) return PROGRAM_ERRORS.get(code).msg;
  if (ANCHOR_ERRORS[code]) return ANCHOR_ERRORS[code];
  return null;
}

/** Best-effort readable message from anything thrown around a transaction. */
export function describeError(e, logs) {
  if (!e) return "Unknown error.";
  if (e instanceof TxError) return e.message;
  // Phantom user rejection
  if (e.code === 4001 || /User rejected/i.test(e.message || "")) return "You cancelled the request in your wallet.";
  const allLogs = logs || e.logs || [];
  const joined = [e.message || String(e), ...allLogs].join("\n");
  const m = joined.match(/Error Number: (\d+)/) || joined.match(/custom program error: 0x([0-9a-f]+)/i);
  if (m) {
    const code = m[0].startsWith("Error Number") ? Number(m[1]) : parseInt(m[1], 16);
    const msg = messageForCode(code);
    if (msg) return msg;
  }
  for (const [re, msg] of SYSTEM_HINTS) if (re.test(joined)) return msg;
  const raw = (e.message || String(e)).split("\n")[0];
  return raw.length > 180 ? raw.slice(0, 180) + "..." : raw;
}

/** Message for a failed simulation or confirmed-with-error status. */
export function describeTxErr(err, logs) {
  const code = customCodeFromErr(err);
  const byCode = messageForCode(code);
  // System program error 1 = insufficient lamports, Token program 1 = insufficient funds
  if (!byCode && code === 1) return "Not enough SOL (or tokens) in your wallet for this transaction.";
  if (byCode) return byCode;
  if (err === "InsufficientFundsForRent" || err?.InsufficientFundsForRent) return "Not enough SOL to cover account rent.";
  if (err === "AccountNotFound") return "Your wallet has no SOL on this network. Fund it first.";
  return describeError(new Error(typeof err === "string" ? err : JSON.stringify(err)), logs);
}
